import { FunctionComponent, useState } from 'react';
import { FormTitle, TextArea } from '@molecules/TextArea/TextArea';
import { NavigationButtons } from '@molecules/TextArea/TextArea';
import { useNavigate } from 'react-router-dom';
import { RoutesList } from '../../../routes/Routes';
import { useWasteStore } from '@waste/store/store';
import { ErrorMessages } from '@types';
import { TextAreaMaxLengthSchema, parseZodErrors } from '@utils';
import { ZodError } from 'zod';
import { ErrorSummary } from '@atoms/ErrorSummary/ErrorSummary';

const moreDetailsLabelText =
  'Can you give more detail to help us find it? (optional)';
const moreDetailsDescription =
  'For example, overflowing bins on the corner next to the post box';
const moreDetailsErrorSummary = 'Details field must be 500 characters or less';

const anythingElseLabelText =
  'Is there anything else we need to know? (optional)';
const anythingElseDescription =
  'For example, is there anything dangerous our officers should know before they visit';
const anythingElseErrorSummary =
  'Additional information field must be 500 characters or less';

const errorSummaryMessages = (errorMessages: ErrorMessages[]) =>
  errorMessages.map((error) => {
    if (error.path === 'moreDetails') {
      return { ...error, message: moreDetailsErrorSummary };
    }
    if (error.path === 'anythingElse') {
      return { ...error, message: anythingElseErrorSummary };
    }
    return error;
  });

export const AdditionalDetails: FunctionComponent = () => {
  const {
    canYouGiveMoreDetail,
    anythingElseWeNeedToKnow,
    updateCanYouGiveMoreDetail,
    updateAnythingElseWeNeedToKnow,
  } = useWasteStore();
  const navigate = useNavigate();
  const formTitle = 'Additional Details';
  const [errorMessages, setErrorMessages] = useState<ErrorMessages[]>([]);
  const MAX_LENGTH = 500;

  const handleChangeMoreDetails = (
    event: React.ChangeEvent<HTMLTextAreaElement>,
  ) => {
    updateCanYouGiveMoreDetail(event.target.value);
    setErrorMessages([]);
  };

  const handleChangeAnythingElse = (
    event: React.ChangeEvent<HTMLTextAreaElement>,
  ) => {
    updateAnythingElseWeNeedToKnow(event.target.value);
    setErrorMessages([]);
  };

  const buttonNext = {
    onClick: () => {
      try {
        TextAreaMaxLengthSchema({
          fieldNames: ['moreDetails', 'anythingElse'],
          maxLength: MAX_LENGTH,
        }).parse({
          moreDetails: canYouGiveMoreDetail,
          anythingElse: anythingElseWeNeedToKnow,
        });
        navigate(RoutesList.EMAIL_CONFIRMATION_REPORT);
      } catch (error) {
        const validationErrors = parseZodErrors(error as ZodError);
        setErrorMessages(
          validationErrors.map((error: ErrorMessages) => ({
            ...error,
            href: error.path,
          })),
        );
      }
    },
  };

  const buttonPrevious = {
    label: 'Previous',
    onClick: () => navigate(RoutesList.WASTE_LOCATION),
  };

  return (
    <>
      <FormTitle text={formTitle} />
      <ErrorSummary messages={errorSummaryMessages(errorMessages)} />
      <TextArea
        labelText={moreDetailsLabelText}
        isLabelTextTitle={false}
        description={moreDetailsDescription}
        value={canYouGiveMoreDetail}
        onChange={handleChangeMoreDetails}
        errorPath={'moreDetails'}
        errorMessages={errorMessages}
      />
      <TextArea
        labelText={anythingElseLabelText}
        isLabelTextTitle={false}
        description={anythingElseDescription}
        value={anythingElseWeNeedToKnow}
        onChange={handleChangeAnythingElse}
        errorPath={'anythingElse'}
        errorMessages={errorMessages}
      />
      <NavigationButtons
        buttonNext={buttonNext}
        buttonPrevious={buttonPrevious}
      />
    </>
  );
};
